import { useCallback, useEffect, useState } from 'react';

export interface UsePromiseArgs<T extends object> {
	fetcher: () => Promise<T>;
}

export interface UsePromiseResult<T> {
	data: T | null;
	loading: boolean;
	error: string | null;
}

export const usePromise = <T extends object>(args: UsePromiseArgs<T>): UsePromiseResult<T> => {
	const { fetcher } = args;

	const [data, setData] = useState<{
		data: T | null;
		loading: boolean;
		error: string | null;
	}>({
		data: null,
		loading: true,
		error: null,
	});

	const fetchData = useCallback(async () => {
		setData((prev) => ({
			...prev,
			loading: true,
			error: null,
		}));

		try {
			const data = await fetcher();

			setData((prev) => ({
				...prev,
				loading: false,
				data,
			}));
		} catch (err) {
			setData((prev) => ({
				loading: false,
				error: err.message,
				data: null,
			}));
		}
	}, [fetcher]);

	useEffect(() => {
		fetchData();
	}, [fetchData]);

	return data;
};
